import { createColumnHelper } from '@tanstack/react-table';
import { LocalizedText } from '@mews/framework';
import { WithAccountId } from '@mews-commander/types';
import { ConvertedCurrencyValue, Dash, LocalizedDateTimeFromUtc } from '@mews-commander/ui';
import { TextKey } from '@mews-commander/enums';
import { PaymentRequestState, PaymentRequestTableRecord } from '@mews-commander/data-access';
import { EmptyPaymentMethodStatusTag } from '../PaymentMethods/EmptyPaymentMethodStatusTag';
import { PaymentRequestActionsMenu } from './PaymentRequestActionsMenu';
import { PaymentRequestsTableReasonCell } from './PaymentRequestsTableReasonCell';
import { PaymentRequestMethodCell } from './PaymentRequestMethodCell';
import { PaymentRequestStateTag } from './PaymentRequestStateTag';

const columnHelper = createColumnHelper<PaymentRequestTableRecord>();

export const getPaymentRequestsColumns = ({
    accountId,
    isCompany = false,
    isPaymentRequestCollectTypeEnabled = false,
}: {
    isCompany?: boolean;
    isPaymentRequestCollectTypeEnabled?: boolean;
} & WithAccountId) => [
    columnHelper.accessor('amount', {
        header: () => <LocalizedText textKey={TextKey.Amount} />,
        cell: ({
            row: {
                original: { amount },
            },
        }) => {
            return amount ? <ConvertedCurrencyValue amount={amount} /> : <Dash />;
        },
    }),
    ...(isPaymentRequestCollectTypeEnabled
        ? [
              columnHelper.accessor('paymentMethod', {
                  header: () => <LocalizedText textKey={TextKey.PaymentMethod} />,
                  cell: ({
                      row: {
                          original: { paymentMethod, state },
                      },
                  }) =>
                      paymentMethod && paymentMethod.paymentMethodId && state === PaymentRequestState.Completed ? (
                          <PaymentRequestMethodCell
                              paymentMethodId={paymentMethod.paymentMethodId}
                              accountId={accountId}
                              paymentMethodType={paymentMethod.paymentMethodType}
                              creditCardType={paymentMethod.creditCardType}
                              identifierLast4Digits={paymentMethod.identifierLast4Digits}
                          />
                      ) : (
                          <EmptyPaymentMethodStatusTag paymentRequestState={state} outline />
                      ),
              }),
          ]
        : []),
    columnHelper.accessor('expirationUtc', {
        header: () => <LocalizedText textKey={TextKey.Expiration} />,
        cell: ({
            row: {
                original: { expirationUtc },
            },
        }) => <LocalizedDateTimeFromUtc utcString={expirationUtc} />,
    }),
    columnHelper.accessor('createdUtc', {
        header: () => <LocalizedText textKey={TextKey.Created} />,
        cell: ({
            row: {
                original: { createdUtc },
            },
        }) => <LocalizedDateTimeFromUtc utcString={createdUtc} />,
    }),
    columnHelper.accessor('state', {
        header: () => <LocalizedText textKey={TextKey.Status} />,
        cell: ({
            row: {
                original: { state },
            },
        }: {
            row: { original: { state: PaymentRequestState } };
        }) => <PaymentRequestStateTag state={state} outline />,
    }),
    columnHelper.accessor('reason', {
        header: () => <LocalizedText textKey={TextKey.Reason} />,
        cell: ({
            row: {
                original: { type, reason },
            },
        }) => <PaymentRequestsTableReasonCell type={type} reason={reason} />,
    }),
    columnHelper.accessor('notes', {
        header: () => <LocalizedText textKey={TextKey.InternalNote} />,
    }),
    columnHelper.accessor('description', {
        header: () => <LocalizedText textKey={TextKey.MessageForCustomer} />,
    }),
    columnHelper.accessor('id', {
        header: () => <LocalizedText textKey={TextKey.ActionPlural} />,
        meta: {
            align: 'end',
        },
        cell: ({ row: { original: paymentRequest } }) => (
            <PaymentRequestActionsMenu accountId={accountId} paymentRequest={paymentRequest} isCompany={isCompany} />
        ),
    }),
];
